import StarIcon from "@mui/icons-material/Star";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosHttpClient } from "../../../../../../services/AxiosHttpService";

export interface ReviewPopupProps {
  popupActive: string;
  setPopupActive: React.Dispatch<React.SetStateAction<string>>;
  callback?: () => void;
}

const SubmitReview: React.FC<ReviewPopupProps> = ({
  popupActive,
  setPopupActive,
  callback,
}) => {
  const submitReview = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    AxiosHttpClient.post("api/v1/docuveritas/users/reviews", {
      expertId: popupActive,
      rating: rating,
      comment: comment,
    })
      .then(() => {
        setIsSubmitting(false);
        callback && callback();
        setPopupActive("");
      })
      .catch((err) => {
        setIsSubmitting(false);
      });
  };
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  return (
    <Dialog
      open={popupActive !== ""}
      onClose={() => setPopupActive("")}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "hsl(var(--card))",
          color: "hsl(var(--card-foreground))",
        },
      }}
    >
      <DialogTitle 
        sx={{ 
          fontSize: "1.25rem", 
          fontWeight: "bold",
          color: "hsl(var(--foreground))",
          backgroundColor: "hsl(var(--card))",
        }}
      >
        {t("SUBMIT_REVIEW_TITLE")}
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "hsl(var(--card))" }}>
        <Box
          component="form"
          onSubmit={submitReview}
          sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Box>
            <Typography sx={{ 
              fontWeight: 500, 
              mb: 1,
              color: "hsl(var(--foreground))",
            }}>
              {t("SUBMIT_REVIEW_LABEL_RATING")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarIcon
                  key={star}
                  onClick={() => setRating(star)}
                  sx={{
                    fontSize: 32,
                    cursor: "pointer",
                    color: rating >= star ? "#fbbf24" : "hsl(var(--muted-foreground))",
                    "&:hover": {
                      color: "#fbbf24",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography sx={{ 
              fontWeight: 500, 
              mb: 1,
              color: "hsl(var(--foreground))",
            }}>
              {t("SUBMIT_REVIEW_LABEL_COMMENT")}
            </Typography>
            <TextField
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("SUBMIT_REVIEW_PLACEHOLDER_COMMENT")}
              multiline
              rows={4}
              required
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "hsl(var(--background))",
                  color: "hsl(var(--foreground))",
                  "& fieldset": {
                    borderColor: "hsl(var(--border))",
                  },
                  "&:hover fieldset": {
                    borderColor: "hsl(var(--ring))",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "hsl(var(--primary))",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "hsl(var(--muted-foreground))",
                },
                "& .MuiOutlinedInput-input::placeholder": {
                  color: "hsl(var(--muted-foreground))",
                  opacity: 1,
                },
              }}
            />
          </Box>

          <DialogActions sx={{ px: 0 }}>
            <Button
              onClick={() => setPopupActive("")}
              variant="outlined"
              disabled={isSubmitting}
              sx={{ 
                textTransform: "none",
                borderColor: "hsl(var(--border))",
                color: "hsl(var(--foreground))",
                "&:hover": {
                  borderColor: "hsl(var(--primary))",
                  backgroundColor: "hsl(var(--muted))",
                },
              }}
            >
              {t("SUBMIT_REVIEW_BUTTON_CANCEL")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={rating === 0 || !comment || isSubmitting}
              sx={{ 
                textTransform: "none",
                backgroundColor: "hsl(var(--primary))",
                color: "hsl(var(--primary-foreground))",
                "&:hover": {
                  backgroundColor: "hsl(var(--primary))",
                  opacity: 0.9,
                },
                "&:disabled": {
                  backgroundColor: "hsl(var(--muted))",
                  color: "hsl(var(--muted-foreground))",
                },
              }}
            >
              {t("SUBMIT_REVIEW_BUTTON_SUBMIT")}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitReview;
